/**
 * file.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";

export interface StoredFile {
  _id: string,
  accountID: string,
  groupID: string,
  uploadUserID: string,
  keyID: string,
  nonce: Uint8Array,
  authtag: Uint8Array,
  bucket: string,
  path: string,
  filename: string,
  filesize: number,
  created?: DateTime,
  contentType: string
}

export function sizeDisplay (bytes: number, dp = 3): string {
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) { return bytes + " B";}
  const units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let u = -1;
  const r = 10**dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
  return bytes.toFixed(dp) + " " + units[u];
}

export function classForType (contentType: string): string {
  switch (contentType) {
    case "application/csv":    return "fa-file-csv";
    case "application/json":   return "fa-file-code";
    case "application/pdf":    return "fa-file-pdf";
    //case "application/rtf":    return "fa-file";
    case "application/x-bzip": return "fa-file-archive";
    case "application/x-bzip2": return "fa-file-archive";
    case "application/gzip": return "fa-file-archive";
    case "application/x-gzip": return "fa-file-archive";
    case "application/zip":    return "fa-file-archive";
    case "application/x-tar":  return "fa-file-archive";
    case "application/xml":    return "fa-file-code";
    case "application/x-7z-compressed": return "fa-file-archive";
    case "application/vnd.rar": return "fa-file-archive";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": return "fa-file-excel";
    case "application/vnd.ms-excel.sheet.binary.macroEnabled.12": return "fa-file-excel";
    case "application/vnd.ms-excel": return "fa-file-excel";
    case "application/vnd.ms-excel.sheet.macroEnabled.12": return "fa-file-excel";
    case "application/msword": return "fa-file-word";
    case "application/vnd.ms-word.document.macroEnabled.12": return "fa-file-word";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document": return "fa-file-word";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.template": return "fa-file-word";
    case "application/vnd.ms-word.template.macroEnabled.12": return "fa-file-word";
    case "application/vnd.ms-powerpoint.template.macroEnabled.12": return "fa-file-powerpoint";
    case "application/vnd.openxmlformats-officedocument.presentationml.template": return "fa-file-powerpoint";
    case "application/vnd.ms-powerpoint.addin.macroEnabled.12": return "fa-file-powerpoint";
    case "application/vnd.openxmlformats-officedocument.presentationml.slideshow": return "fa-file-powerpoint";
    case "application/vnd.ms-powerpoint.slideshow.macroEnabled.12": return "fa-file-powerpoint";
    case "application/vnd.ms-powerpoint": return "fa-file-powerpoint";
    case "application/vnd.ms-powerpoint.presentation.macroEnabled.12": return "fa-file-powerpoint";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation": return "fa-file-powerpoint";
    case "audio/3gpp":  return "fa-file-audio";
    case "audio/3gpp2": return "fa-file-audio";
    case "audio/aac":   return "fa-file-audio";
    case "audio/ogg":   return "fa-file-audio";
    case "audio/opus":  return "fa-file-audio";
    case "audio/mpeg":  return "fa-file-audio";
    case "audio/midi":  return "fa-file-audio";
    case "audio/x-midi": return "fa-file-audio";
    case "audio/x-m4a": return "fa-file-audio";
    case "audio/wav":   return "fa-file-audio";
    case "audio/webm":  return "fa-file-audio";
    case "image/bmp":   return "fa-file-image";
    case "image/jpeg":  return "fa-file-image";
    case "image/png":   return "fa-file-image";
    case "image/gif":   return "fa-file-image";
    case "image/svg+xml": return "fa-file-image";
    case "image/tiff":  return "fa-file-image";
    case "image/webp":  return "fa-file-image";
    case "text/calendar": return "fa-calendar";
    case "text/css":    return "fa-file-code";
    case "text/csv":    return "fa-file-csv";
    case "text/javascript": return "fa-file-code";
    case "text/markdown": return "fa-file-code";
    //case "text/plain":  return "fa-file";
    //case "text/rtf":    return "fa-file";
    case "text/xml":    return "fa-file-code";
    case "text/html":   return "fa-file-code";
    case "video/3gpp":  return "fa-file-video";
    case "video/3gpp2":  return "fa-file-video";
    case "video/mpeg":  return "fa-file-video";
    case "video/mp4":   return "fa-file-video";
    case "video/ogg":   return "fa-file-video";
    case "video/m4v":   return "fa-file-video";
    case "video/mp2t":  return "fa-file-video";
    case "video/webm":  return "fa-file-video";
    case "video/x-msvideo": return "fa-file-video";

    default:
      return "fa-file";
    }
}